import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export type CommandDashboardPeriod = "today" | "week" | "month";
export type DashboardTone = "info" | "warn" | "critical" | "positive" | "neutral" | "caution";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface CommandDashboardStrategicSummary {
  period: CommandDashboardPeriod;
  totalLeads: number;
  wonCount: number;
  bookedRevenue: number;
  pendingRevenue: number;
  netJobs: number;
  netRevenue: number;
  installsScheduled: number;
  missingResults: number;
  overdueFollowups: number;
  reschedulesNeeded: number;
  avgCpa: number;
  clusteringPct: number;
}

export interface CommandDashboardActionRow {
  actionId: string;
  label: string;
  itemCount: number;
  topName: string;
  topReason: string;
  topPhone: string;
  primaryCta: string;
  route: string;
  severity: "info" | "warn" | "critical";
}

export interface CommandDashboardChannelRow {
  id: string;
  channelName: string;
  leads: number;
  sold: number;
  netJobs: number;
  sourceCost: number;
  cpa: number | null;
  leadQuality: number;
  status: "Healthy" | "Needs Attention" | "Underperforming";
}

export interface CommandDashboardRepRow {
  id: string;
  repName: string;
  sampleSize: number;
  bookedCount: number;
  completedCount: number;
  conversionRate: number;
  insightTone: "positive" | "neutral" | "caution";
  insight: string;
}

export interface CommandDashboardOperationsRow {
  jobId: string;
  jobNumber: string;
  customerName: string;
  customerPhone: string;
  city: string;
  currentStage: string;
  projectType: string;
  materialReadiness: string;
  permitStatus: string;
  assignmentType: string;
  assignedCrew: string;
  assignedSubcontractor: string;
  dispatchStatus: string;
  scheduledInstallDate: string;
  scheduledDate: string;
  daysUntil: number | null;
  scheduledBucket: "today" | "tomorrow" | "upcoming" | "unscheduled";
  readinessStatus: "Ready for Install" | "Partially Ready" | "Not Ready";
  lifecycle: "blocked" | "in_progress" | "scheduled" | "unscheduled";
  isBlocked: boolean;
  needsAction: boolean;
  blockers: string[];
  blockerReason: string;
  openIssueCount: number;
  revenueAtRisk: number;
  intakeReviewRequired: boolean;
  approvedForOrdering: boolean;
  planningStatus: string;
  nextAction: string;
  nextActionRoute: string;
  priority: "critical" | "high" | "medium" | "low";
  staleDays: number;
  sortAt: string;
}

export interface CommandDashboardOperationsSummary {
  activeJobs: number;
  jobsToday: number;
  blockedJobs: number;
  atRisk48h: number;
  notReadyJobs: number;
  openIssues: number;
  todayJobs: number;
  tomorrowJobs: number;
  unscheduledJobs: number;
  inProgressJobs: number;
  blockedRevenue: number;
  atRiskRevenue: number;
  unscheduledRevenue: number;
  criticalJobs: number;
  orderMaterialsCount: number;
  submitPermitsCount: number;
  assignCrewCount: number;
  resolveIssuesCount: number;
  intakeReviewRequiredCount: number;
}

export interface CommandDashboardStrategicBundle {
  summary: CommandDashboardStrategicSummary;
  actions: CommandDashboardActionRow[];
  channels: CommandDashboardChannelRow[];
  reps: CommandDashboardRepRow[];
}

export interface CommandDashboardOperationsBundle {
  summary: CommandDashboardOperationsSummary;
  rows: CommandDashboardOperationsRow[];
  count: number;
}

type StrategicSummaryRow = {
  period_key?: string | null;
  total_leads?: number | string | null;
  won_count?: number | string | null;
  booked_revenue?: number | string | null;
  pending_revenue?: number | string | null;
  net_jobs?: number | string | null;
  net_revenue?: number | string | null;
  installs_scheduled?: number | string | null;
  missing_results?: number | string | null;
  overdue_followups?: number | string | null;
  reschedules_needed?: number | string | null;
  avg_cpa?: number | string | null;
  clustering_pct?: number | string | null;
};

type ActionRow = {
  action_id?: string | null;
  label?: string | null;
  item_count?: number | string | null;
  top_name?: string | null;
  top_reason?: string | null;
  top_phone?: string | null;
  primary_cta?: string | null;
  route?: string | null;
  severity?: string | null;
};

type ChannelRow = {
  id?: string | null;
  channel_name?: string | null;
  leads?: number | string | null;
  sold?: number | string | null;
  net_jobs?: number | string | null;
  source_cost?: number | string | null;
  cpa?: number | string | null;
  lead_quality?: number | string | null;
  status?: string | null;
};

type RepRow = {
  id?: string | null;
  rep_name?: string | null;
  sample_size?: number | string | null;
  booked_count?: number | string | null;
  completed_count?: number | string | null;
  conversion_rate?: number | string | null;
  insight_tone?: string | null;
  insight?: string | null;
};

type OperationsRow = {
  job_id?: string | null;
  job_number?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  city?: string | null;
  current_stage?: string | null;
  project_type?: string | null;
  material_readiness?: string | null;
  permit_status?: string | null;
  assignment_type?: string | null;
  assigned_crew?: string | null;
  assigned_subcontractor?: string | null;
  dispatch_status?: string | null;
  scheduled_install_date?: string | null;
  scheduled_date?: string | null;
  days_until?: number | string | null;
  scheduled_bucket?: string | null;
  readiness_status?: string | null;
  lifecycle?: string | null;
  is_blocked?: boolean | null;
  needs_action?: boolean | null;
  blockers?: string[] | null;
  blocker_reason?: string | null;
  open_issue_count?: number | string | null;
  revenue_at_risk?: number | string | null;
  intake_review_required?: boolean | null;
  approved_for_ordering?: boolean | null;
  planning_status?: string | null;
  next_action?: string | null;
  next_action_route?: string | null;
  priority?: string | null;
  stale_days?: number | string | null;
  sort_at?: string | null;
};

type OperationsSummaryRow = {
  active_jobs?: number | string | null;
  jobs_today?: number | string | null;
  blocked_jobs?: number | string | null;
  at_risk_48h?: number | string | null;
  not_ready_jobs?: number | string | null;
  open_issues?: number | string | null;
  today_jobs?: number | string | null;
  tomorrow_jobs?: number | string | null;
  unscheduled_jobs?: number | string | null;
  in_progress_jobs?: number | string | null;
  blocked_revenue?: number | string | null;
  at_risk_revenue?: number | string | null;
  unscheduled_revenue?: number | string | null;
  critical_jobs?: number | string | null;
  order_materials_count?: number | string | null;
  submit_permits_count?: number | string | null;
  assign_crew_count?: number | string | null;
  resolve_issues_count?: number | string | null;
  intake_review_required_count?: number | string | null;
};

const STRATEGIC_SUMMARY_COLUMNS = "period_key,total_leads,won_count,booked_revenue,pending_revenue,net_jobs,net_revenue,installs_scheduled,missing_results,overdue_followups,reschedules_needed,avg_cpa,clustering_pct";
const ACTION_COLUMNS = "action_id,label,item_count,top_name,top_reason,top_phone,primary_cta,route,severity,action_sort";
const CHANNEL_COLUMNS = "id,channel_name,leads,sold,net_jobs,source_cost,cpa,lead_quality,status,sort_cpa,sort_sold";
const REP_COLUMNS = "id,rep_name,sample_size,booked_count,completed_count,conversion_rate,insight_tone,insight";
const OPERATIONS_COLUMNS = "job_id,job_number,customer_name,customer_phone,city,current_stage,project_type,material_readiness,permit_status,assignment_type,assigned_crew,assigned_subcontractor,dispatch_status,scheduled_install_date,scheduled_date,days_until,scheduled_bucket,readiness_status,lifecycle,is_blocked,needs_action,blockers,blocker_reason,open_issue_count,revenue_at_risk,intake_review_required,approved_for_ordering,planning_status,next_action,next_action_route,priority,stale_days,sort_at";
const OPERATIONS_SUMMARY_COLUMNS = "active_jobs,jobs_today,blocked_jobs,at_risk_48h,not_ready_jobs,open_issues,today_jobs,tomorrow_jobs,unscheduled_jobs,in_progress_jobs,blocked_revenue,at_risk_revenue,unscheduled_revenue,critical_jobs,order_materials_count,submit_permits_count,assign_crew_count,resolve_issues_count,intake_review_required_count";

function num(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function text(value: unknown, fallback = ""): string {
  return value == null ? fallback : String(value);
}

function arrayText(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function mapPeriod(value: unknown): CommandDashboardPeriod {
  return value === "today" || value === "week" || value === "month" ? value : "month";
}

function mapActionSeverity(value: unknown): CommandDashboardActionRow["severity"] {
  return value === "critical" || value === "warn" || value === "info" ? value : "info";
}

function mapChannelStatus(value: unknown): CommandDashboardChannelRow["status"] {
  if (value === "Healthy" || value === "Needs Attention" || value === "Underperforming") return value;
  return "Needs Attention";
}

function mapInsightTone(value: unknown): CommandDashboardRepRow["insightTone"] {
  return value === "positive" || value === "neutral" || value === "caution" ? value : "neutral";
}

function mapBucket<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  const candidate = text(value) as T;
  return allowed.includes(candidate) ? candidate : fallback;
}

function mapStrategicSummary(row: StrategicSummaryRow | null | undefined, period: CommandDashboardPeriod): CommandDashboardStrategicSummary {
  return {
    period: mapPeriod(row?.period_key ?? period),
    totalLeads: num(row?.total_leads),
    wonCount: num(row?.won_count),
    bookedRevenue: num(row?.booked_revenue),
    pendingRevenue: num(row?.pending_revenue),
    netJobs: num(row?.net_jobs),
    netRevenue: num(row?.net_revenue),
    installsScheduled: num(row?.installs_scheduled),
    missingResults: num(row?.missing_results),
    overdueFollowups: num(row?.overdue_followups),
    reschedulesNeeded: num(row?.reschedules_needed),
    avgCpa: num(row?.avg_cpa),
    clusteringPct: num(row?.clustering_pct),
  };
}

function mapAction(row: ActionRow): CommandDashboardActionRow {
  return {
    actionId: text(row.action_id),
    label: text(row.label),
    itemCount: num(row.item_count),
    topName: text(row.top_name),
    topReason: text(row.top_reason),
    topPhone: text(row.top_phone),
    primaryCta: text(row.primary_cta, "Open"),
    route: text(row.route, "/command-dashboard"),
    severity: mapActionSeverity(row.severity),
  };
}

function mapChannel(row: ChannelRow): CommandDashboardChannelRow {
  const rawCpa = row.cpa == null ? null : Number(row.cpa);
  return {
    id: text(row.id, text(row.channel_name)),
    channelName: text(row.channel_name, "Unattributed"),
    leads: num(row.leads),
    sold: num(row.sold),
    netJobs: num(row.net_jobs),
    sourceCost: num(row.source_cost),
    cpa: Number.isFinite(rawCpa) ? rawCpa : null,
    leadQuality: num(row.lead_quality),
    status: mapChannelStatus(row.status),
  };
}

function mapRep(row: RepRow): CommandDashboardRepRow {
  return {
    id: text(row.id, text(row.rep_name)),
    repName: text(row.rep_name, "Unassigned"),
    sampleSize: num(row.sample_size),
    bookedCount: num(row.booked_count),
    completedCount: num(row.completed_count),
    conversionRate: num(row.conversion_rate),
    insightTone: mapInsightTone(row.insight_tone),
    insight: text(row.insight),
  };
}

function mapOperationsRow(row: OperationsRow): CommandDashboardOperationsRow {
  return {
    jobId: text(row.job_id),
    jobNumber: text(row.job_number, text(row.job_id)),
    customerName: text(row.customer_name, "Unknown"),
    customerPhone: text(row.customer_phone),
    city: text(row.city),
    currentStage: text(row.current_stage, "Sale Closed"),
    projectType: text(row.project_type, "Other"),
    materialReadiness: text(row.material_readiness, "Unknown"),
    permitStatus: text(row.permit_status, "Not Required"),
    assignmentType: text(row.assignment_type, "Unassigned"),
    assignedCrew: text(row.assigned_crew),
    assignedSubcontractor: text(row.assigned_subcontractor),
    dispatchStatus: text(row.dispatch_status),
    scheduledInstallDate: text(row.scheduled_install_date),
    scheduledDate: text(row.scheduled_date),
    daysUntil: row.days_until == null ? null : num(row.days_until),
    scheduledBucket: mapBucket(row.scheduled_bucket, ["today", "tomorrow", "upcoming", "unscheduled"] as const, "unscheduled"),
    readinessStatus: mapBucket(row.readiness_status, ["Ready for Install", "Partially Ready", "Not Ready"] as const, "Not Ready"),
    lifecycle: mapBucket(row.lifecycle, ["blocked", "in_progress", "scheduled", "unscheduled"] as const, "unscheduled"),
    isBlocked: row.is_blocked === true,
    needsAction: row.needs_action === true,
    blockers: arrayText(row.blockers),
    blockerReason: text(row.blocker_reason),
    openIssueCount: num(row.open_issue_count),
    revenueAtRisk: num(row.revenue_at_risk),
    intakeReviewRequired: row.intake_review_required === true,
    approvedForOrdering: row.approved_for_ordering === true,
    planningStatus: text(row.planning_status),
    nextAction: text(row.next_action, "Open Job"),
    nextActionRoute: text(row.next_action_route, `/jobs/${text(row.job_id)}`),
    priority: mapBucket(row.priority, ["critical", "high", "medium", "low"] as const, "low"),
    staleDays: num(row.stale_days),
    sortAt: text(row.sort_at),
  };
}

function mapOperationsSummary(row: OperationsSummaryRow | null | undefined): CommandDashboardOperationsSummary {
  return {
    activeJobs: num(row?.active_jobs),
    jobsToday: num(row?.jobs_today ?? row?.today_jobs),
    blockedJobs: num(row?.blocked_jobs),
    atRisk48h: num(row?.at_risk_48h),
    notReadyJobs: num(row?.not_ready_jobs),
    openIssues: num(row?.open_issues),
    todayJobs: num(row?.today_jobs),
    tomorrowJobs: num(row?.tomorrow_jobs),
    unscheduledJobs: num(row?.unscheduled_jobs),
    inProgressJobs: num(row?.in_progress_jobs),
    blockedRevenue: num(row?.blocked_revenue),
    atRiskRevenue: num(row?.at_risk_revenue),
    unscheduledRevenue: num(row?.unscheduled_revenue),
    criticalJobs: num(row?.critical_jobs),
    orderMaterialsCount: num(row?.order_materials_count),
    submitPermitsCount: num(row?.submit_permits_count),
    assignCrewCount: num(row?.assign_crew_count),
    resolveIssuesCount: num(row?.resolve_issues_count),
    intakeReviewRequiredCount: num(row?.intake_review_required_count),
  };
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({
    key,
    durationMs: Date.now() - startedAt,
    payloadBytes,
    cacheHit: false,
  });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

export const CommandDashboardPageService = {
  async getStrategicBundle(query: { period: CommandDashboardPeriod; refreshToken?: number }): Promise<{ data: CommandDashboardStrategicBundle; error: unknown | null }> {
    const key = `command-dashboard.strategic.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const [summaryRes, actionsRes, channelsRes, repsRes] = await Promise.all([
          supabase
            .from("v_command_dashboard_strategic_summary")
            .select(STRATEGIC_SUMMARY_COLUMNS)
            .eq("period_key", query.period)
            .maybeSingle(),
          supabase
            .from("v_command_dashboard_actions_ui")
            .select(ACTION_COLUMNS)
            .eq("period_key", query.period)
            .order("action_sort", { ascending: true }),
          supabase
            .from("v_command_dashboard_channels_ui")
            .select(CHANNEL_COLUMNS)
            .eq("period_key", query.period)
            .order("sort_cpa", { ascending: true })
            .order("sort_sold", { ascending: false })
            .limit(5),
          supabase
            .from("v_command_dashboard_reps_ui")
            .select(REP_COLUMNS)
            .eq("period_key", query.period)
            .order("conversion_rate", { ascending: false })
            .order("sample_size", { ascending: false })
            .limit(5),
        ]);

        const error = summaryRes.error ?? actionsRes.error ?? channelsRes.error ?? repsRes.error ?? null;
        if (error) throw error;

        return {
          data: {
            summary: mapStrategicSummary(summaryRes.data as StrategicSummaryRow | null, query.period),
            actions: ((actionsRes.data ?? []) as ActionRow[]).map(mapAction),
            channels: ((channelsRes.data ?? []) as ChannelRow[]).map(mapChannel),
            reps: ((repsRes.data ?? []) as RepRow[]).map(mapRep),
          },
          error: null,
        };
      });
    } catch (error) {
      return {
        data: {
          summary: mapStrategicSummary(null, query.period),
          actions: [],
          channels: [],
          reps: [],
        },
        error,
      };
    }
  },

  async getOperationsBundle(query: { page: number; pageSize: number; refreshToken?: number }): Promise<{ data: CommandDashboardOperationsBundle; error: unknown | null }> {
    const key = `command-dashboard.operations.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const page = Math.max(0, Number(query.page) || 0);
        const pageSize = Math.max(1, Math.min(Number(query.pageSize) || 10, 100));
        const from = page * pageSize;
        const to = from + pageSize - 1;
        const [summaryRes, rowsRes] = await Promise.all([
          supabase
            .from("v_command_dashboard_operations_summary")
            .select(OPERATIONS_SUMMARY_COLUMNS)
            .maybeSingle(),
          supabase
            .from("v_command_dashboard_operations_rows_ui")
            .select(OPERATIONS_COLUMNS, { count: "exact" })
            .order("priority_sort", { ascending: true })
            .order("sort_at", { ascending: false })
            .range(from, to),
        ]);
        const error = summaryRes.error ?? rowsRes.error ?? null;
        if (error) throw error;
        return {
          data: {
            summary: mapOperationsSummary(summaryRes.data as OperationsSummaryRow | null),
            rows: ((rowsRes.data ?? []) as OperationsRow[]).map(mapOperationsRow),
            count: rowsRes.count ?? 0,
          },
          error: null,
        };
      });
    } catch (error) {
      return {
        data: {
          summary: mapOperationsSummary(null),
          rows: [],
          count: 0,
        },
        error,
      };
    }
  },
};
